/**
 * Celebration.tsx — the moment straight after a successful payment: a burst of
 * confetti, a cheerful illustration, and (for a guest who still needs an
 * account) a modal that congratulates them, says who looks after them next, and
 * hands them to the account form.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey-ux L5
 * (post-payment celebration, CTO 2026-09-24; v2 the same day — no close, no
 * amounts, larger, covering, trust points).
 *
 * WHY: paying used to swap the card screen for a bare "Create your account"
 * form, so the one moment in the journey worth marking went by unmarked. The
 * celebration marks it, builds confidence with claims the site already makes
 * (`celebrationCopy.ts` — no figures, no payment words on any route), and hands
 * the patient straight to the account form: its one button is the only way out.
 *
 * ── EVERYTHING DECORATIVE IS INVISIBLE TO ASSISTIVE TECH ───────────────────
 * The confetti and the illustration are `aria-hidden`; the words carry the
 * moment. The illustration is drawn INLINE — the CSP blocks external images —
 * from the brand tokens, so it follows the palette without a second copy of it.
 *
 * ── REDUCED MOTION ⇒ NO CONFETTI AT ALL ────────────────────────────────────
 * Not slower confetti, not static confetti: none. The signal is
 * `usePrefersReducedMotion` (`ui/motion`, the estate's ONE reduced-motion
 * source — never a second `matchMedia`), which also treats a browser that
 * cannot answer as "reduce". The global CSS kill in tokens.css is the second
 * lock. The illustration's small arrival pop is skipped the same way.
 *
 * ── THE LAYERS ARE `position: fixed`, SO NOTHING ABOVE THEM MAY TRANSFORM ──
 * The confetti layer and the modal's overlay are both fixed to the viewport
 * and neither is portaled (the confetti sits INSIDE the overlay in the modal).
 * A transform, filter or backdrop-filter on ANY ancestor would re-anchor them
 * to that ancestor instead of the screen (`.gps-plan-breakout` is one; `FadeIn`
 * settles to `transform: none` for the same reason). Never mount these inside
 * a transformed wrapper, and never give the overlay a backdrop-filter: the
 * confetti inside it is fixed too.
 *
 * ── NOTHING HERE IS STORED ON THE DEVICE ───────────────────────────────────
 * "Has this already played?" is answered from the history-state hand-over
 * (`ThankYouNavState.celebrated`) and from this document's own memory
 * ({@link noteCheckoutHandOver}) — never sessionStorage, localStorage or a
 * cookie. A flag that only stops decoration replaying is not "strictly
 * necessary" storage (UK PECR reg. 6), and this repo's rule is that
 * non-essential storage is opt-in. A first cut of this file kept a per-entry
 * "played" key in sessionStorage; it was removed before merge for that reason.
 * Nothing here pushes to the dataLayer either: the funnel events fire exactly
 * where and when they did before this existed.
 */
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { containTab } from '../ui/focusables';
import { KeepCompoundsWhole } from '../ui/KeepCompoundsWhole';
import { ButtonArrow, buttonClass, buttonStyle } from '../ui/kit';
import { usePrefersReducedMotion } from '../ui/motion';
import { celebrationCopy, type CelebrationTrustPoint } from './celebrationCopy';
import type { PaymentCaptureRoute } from './paymentCopy';

/* ── Confetti ────────────────────────────────────────────────────────────── */

type ConfettiPiece = {
  /** Horizontal start, % of the viewport width. */
  left: number;
  delayMs: number;
  durationMs: number;
  /** Sideways travel over the fall, px. */
  driftPx: number;
  /** Total rotation over the fall, degrees. */
  spinDeg: number;
  width: number;
  height: number;
  colour: string;
};

/**
 * The celebration's one colour outside the brand palette. Decoration only —
 * never an ink, so it carries no contrast duty — which is why it is not a
 * shared token: it is a fallback inside a var() nobody else defines.
 */
const GOLD = 'var(--gps-jux-gold, #f2b84b)';

/** Brand colours, plus the gold. */
const CONFETTI_COLOURS = ['var(--gps-primary)', 'var(--gps-cta)', 'var(--gps-coral)', GOLD, 'var(--gps-primary-tint-strong)'];

/**
 * DETERMINISTIC, NOT `Math.random()`: a fixed-seed generator (Park–Miller) lays
 * the pieces out the same way on every render, so a re-render mid-fall cannot
 * reshuffle the burst and a spec can reason about the markup.
 */
function buildConfetti(count: number): ConfettiPiece[] {
  let seed = 20260924;
  const next = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  return Array.from({ length: count }, (_, i) => ({
    left: Math.round(next() * 1000) / 10,
    delayMs: Math.round(next() * 700),
    durationMs: 2200 + Math.round(next() * 1000),
    driftPx: Math.round((next() - 0.5) * 160),
    spinDeg: Math.round(360 + next() * 540) * (next() < 0.5 ? -1 : 1),
    width: 6 + Math.round(next() * 5),
    height: 9 + Math.round(next() * 7),
    colour: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
  }));
}

/** The burst is laid out once, at module load. */
export const CONFETTI_PIECES: readonly ConfettiPiece[] = buildConfetti(44);

/**
 * How long the layer stays mounted: the longest delay + fall, plus a margin. A
 * finished burst is REMOVED rather than left as dozens of invisible nodes over
 * the page — `pointer-events: none` already keeps it from blocking a click, but
 * there is no reason to keep it at all.
 */
export const CONFETTI_LIFETIME_MS = 4200;

/**
 * One burst of confetti over the whole viewport. Decorative (`aria-hidden`),
 * inert to the pointer, gone after {@link CONFETTI_LIFETIME_MS}, and NOT
 * RENDERED AT ALL under reduced motion.
 */
export function Confetti() {
  const reduced = usePrefersReducedMotion();
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (reduced) return;
    const timer = window.setTimeout(() => setFinished(true), CONFETTI_LIFETIME_MS);
    return () => window.clearTimeout(timer);
  }, [reduced]);

  if (reduced || finished) return null;
  return (
    <div className="gps-jux-confetti" aria-hidden="true" data-testid="celebration-confetti">
      {CONFETTI_PIECES.map((piece, i) => (
        <span
          key={i}
          className="gps-jux-confetti-piece"
          style={
            {
              left: `${piece.left}%`,
              width: `${piece.width}px`,
              height: `${piece.height}px`,
              background: piece.colour,
              animationDelay: `${piece.delayMs}ms`,
              animationDuration: `${piece.durationMs}ms`,
              '--gps-jux-confetti-drift': `${piece.driftPx}px`,
              '--gps-jux-confetti-spin': `${piece.spinDeg}deg`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

/* ── The illustration ────────────────────────────────────────────────────── */

/**
 * A trail of steps up a small hill to a flag, a tick for "done", and a few
 * sparkles — "the first step of your journey", drawn. Self-drawn inline SVG in
 * the brand tokens (the CSP blocks external images). Decorative: the heading
 * and the copy beside it say everything it shows.
 */
export function CelebrationArt({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 200 128"
      width={200}
      height={128}
      xmlns="http://www.w3.org/2000/svg"
      data-testid="celebration-art"
    >
      <circle cx="100" cy="70" r="58" style={{ fill: 'var(--gps-primary-light)' }} />
      <path
        d="M26 122 C 50 100, 86 86, 122 88 C 146 89, 164 104, 176 122 Z"
        style={{ fill: 'var(--gps-primary-tint-strong)' }}
      />
      <rect x="20" y="119" width="160" height="7" rx="3.5" style={{ fill: 'var(--gps-primary-tint-strong)' }} />
      {/* The trail, and the steps along it. */}
      <path
        d="M46 121 C 64 116, 60 107, 80 103 S 108 95, 123 90"
        fill="none"
        strokeWidth={8}
        strokeLinecap="round"
        style={{ stroke: 'var(--gps-surface)' }}
      />
      <path
        d="M46 121 C 64 116, 60 107, 80 103 S 108 95, 123 90"
        fill="none"
        strokeWidth={3.2}
        strokeLinecap="round"
        strokeDasharray="0.1 8.5"
        style={{ stroke: 'var(--gps-primary)' }}
      />
      {/* The flag at the top. */}
      <path d="M124 91 V 50" strokeWidth={3} strokeLinecap="round" style={{ stroke: 'var(--gps-primary-dark)' }} />
      <path
        d="M125.5 51 L 151 58.5 L 125.5 66 Z"
        strokeWidth={1.5}
        strokeLinejoin="round"
        style={{ fill: 'var(--gps-cta)', stroke: 'var(--gps-cta)' }}
      />
      {/* Done. */}
      <circle cx="62" cy="56" r="17" style={{ fill: 'var(--gps-primary)' }} />
      <path
        d="M54 56.5 L 59.5 62 L 71 50.5"
        fill="none"
        strokeWidth={3.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ stroke: 'var(--gps-on-accent)' }}
      />
      {/* Sparkles. */}
      <path d="M156 24 Q 156 32 164 32 Q 156 32 156 40 Q 156 32 148 32 Q 156 32 156 24 Z" style={{ fill: GOLD }} />
      <path d="M36 80 Q 36 86 42 86 Q 36 86 36 92 Q 36 86 30 86 Q 36 86 36 80 Z" style={{ fill: 'var(--gps-coral)' }} />
      <path
        d="M170 76 Q 170 80.5 174.5 80.5 Q 170 80.5 170 85 Q 170 80.5 165.5 80.5 Q 170 80.5 170 76 Z"
        style={{ fill: 'var(--gps-primary)' }}
      />
      <path d="M98 14 Q 98 18 102 18 Q 98 18 98 22 Q 98 18 94 18 Q 98 18 98 14 Z" style={{ fill: 'var(--gps-coral)' }} />
      <circle cx="142" cy="104" r="2.4" style={{ fill: GOLD }} />
      <circle cx="30" cy="48" r="2.2" style={{ fill: GOLD }} />
      <circle cx="178" cy="54" r="2" style={{ fill: 'var(--gps-coral)' }} />
    </svg>
  );
}

/* ── The modal ───────────────────────────────────────────────────────────── */

/**
 * The trust points' icons: a shield with a tick (the clinical review), a parcel
 * (the pharmacy and the plain packaging) and a speech bubble (the team).
 * Decorative (`aria-hidden`) — each sits beside a bold line that says the same
 * thing in words — and drawn inline in `currentColor` (the CSP blocks external
 * images; the chip around it sets the colour from the brand tokens).
 */
function TrustIcon({ icon }: { icon: CelebrationTrustPoint['icon'] }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      width={20}
      height={20}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icon === 'clinician' ? (
        <>
          <path d="M12 3 L19 6 V11.5 C19 15.8 16 19.2 12 21 C8 19.2 5 15.8 5 11.5 V6 Z" />
          <path d="M9 12 L11.2 14.2 L15.2 10.2" />
        </>
      ) : icon === 'pharmacy' ? (
        <>
          <path d="M3.5 7.5 L12 3.5 L20.5 7.5 V16.5 L12 20.5 L3.5 16.5 Z" />
          <path d="M3.5 7.5 L12 11.5 L20.5 7.5" />
          <path d="M12 11.5 V20.5" />
        </>
      ) : (
        <path d="M5 5 H19 A2 2 0 0 1 21 7 V15 A2 2 0 0 1 19 17 H10 L6 20.5 V17 H5 A2 2 0 0 1 3 15 V7 A2 2 0 0 1 5 5 Z" />
      )}
    </svg>
  );
}

/**
 * "Are there words below the pinned row right now?" — MEASURED, the same way
 * the plan rail's `useRailOverflow` (checkout/PlanSelection.tsx) answers it for
 * its own scroller, because the answer depends on the actual box: the screen's
 * height, Safari's toolbars, the zoom, the route's copy and the fonts arriving.
 *  - The panel's `scroll` answers "the patient scrolled".
 *  - A ResizeObserver on the PANEL answers a new screen size, and one on the
 *    BODY answers the content growing inside a box of the same size (the web
 *    fonts landing re-wrap every line, and the panel's own box never changes).
 * 4px of slack, so a sub-pixel remainder never draws an edge over nothing.
 * Flipping the answer only recolours a border and a shadow (no height moves),
 * so it cannot oscillate.
 *
 * Kept here rather than lifted out of PlanSelection with this fix: that file is
 * the plan step's money path, and moving its hook is a change to a screen this
 * review was not about. jsdom has no layout, so the spec stubs the panel's own
 * metrics, as the rail's specs do.
 */
function useMoreBelow(panelRef: RefObject<HTMLElement>, bodyRef: RefObject<HTMLElement>): boolean {
  const [moreBelow, setMoreBelow] = useState(false);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const measure = () => setMoreBelow(panel.scrollHeight - panel.scrollTop - panel.clientHeight > 4);
    measure();
    panel.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    // Guarded: jsdom has no ResizeObserver, and this must not be what stops the
    // modal rendering in a spec (the emergency ribbon guards its own the same way).
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(panel);
    if (bodyRef.current) ro?.observe(bodyRef.current);
    return () => {
      panel.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [panelRef, bodyRef]);

  return moreBelow;
}

/**
 * The celebration modal a GUEST sees the moment checkout moves them to the
 * account step after a fresh, successful attach.
 *
 * ── ONE WAY OUT (v2, CTO 2026-09-24) ───────────────────────────────────────
 * "You shouldn't have a close button here. It shouldn't close if you click
 * outside as well. The only option they have to close it is 'Finish setting up
 * my account'." So:
 *  - NO Close control;
 *  - the overlay does nothing when clicked (and a press on it keeps focus
 *    where it was, instead of dropping it on `<body>`);
 *  - Escape is SWALLOWED — caught in the capture phase and stopped, the
 *    `ConsentBanner` precedent, so a page-level Escape handler underneath
 *    cannot act on a page the patient cannot see;
 *  - the ONE button, "Finish setting up my account", is focused on open and is
 *    always reachable, so nobody is stuck: one press and they are on the form.
 * Tab and Shift+Tab stay inside the panel. With no Escape, a keyboard user who
 * could tab out would be typing into a form hidden under the scrim.
 *
 * ── WHY NOT `DialogShell` ANY MORE ─────────────────────────────────────────
 * The shell's contract is the opposite of this one — Escape and the overlay
 * cancel, focus is not contained, the scrim and panel are inline styles sized
 * for a confirm sentence — and 22+ callers (and `ConfirmDialog.spec`) pin it.
 * Bending it would have meant a non-dismissible flag, a containment mode, a
 * scrim override and a size override on a shared primitive for one caller.
 * `ConsentBanner` made the same call for the same reasons; this copies the
 * shell's grammar rather than its code: `role="dialog"`, `aria-modal`, named by
 * its heading (`useId` + `aria-labelledby`), described by the warm line.
 *
 * ── LARGER, AND IT COVERS THE PAGE ─────────────────────────────────────────
 * A near-opaque brand-ink scrim, a panel up to 40rem wide on a desktop and
 * near-full-screen on a phone (tokens.css, the P6 section). The body of the
 * page is scroll-locked while it is open and restored EXACTLY after.
 *
 * ── THE BUTTON IS PINNED TO THE PANEL'S FOOT ───────────────────────────────
 * The panel scrolls its own content when it is taller than the screen (a
 * landscape phone, 200% zoom). The actions row is `position: sticky` at the
 * bottom of that scroller, so the focused button is ALWAYS on screen (WCAG
 * 2.4.7) and the panel opens at its top — the illustration and
 * "Congratulations!" first. The focus call still asks for `scrollIntoView`
 * (`nearest`), which now finds nothing to do; it stays as the second lock.
 *
 * FOCUS goes to the button on the NEXT TICK, not in this component's mount
 * effect. The account step mounts in the same commit as this dialog, and
 * `CheckoutFlow`'s view reset (`useResetViewOnChange`) is a PARENT effect, so
 * it runs after this child's effects and moves focus to the top of the page —
 * a mount-time focus would be taken straight back out of the modal.
 * (`celebration.spec.tsx` mounts the shell's routed-content region to prove
 * this.) On close the caller puts focus on the account form's first field (see
 * {@link focusAccountEntry}).
 *
 * ── …AND IT SAYS SO WHEN WORDS ARE UNDER IT (review, 2026-09-24) ───────────
 * On a phone the words can be taller than the panel (Safari's own bars can
 * leave a 667px phone far less page than that), and the pinned row then cuts a
 * line in half with nothing to say more follows — iOS shows no scrollbar until
 * the patient scrolls. `useMoreBelow` MEASURES the panel and sets
 * `data-more-below`, which gives the row a hairline and a soft shadow at its top
 * edge while there is content beneath it, and takes them away at the end. The
 * phone layout is also compacted so that on most phones there is nothing under
 * it at all (tokens.css, P6). No "scroll for more" sentence: what sits below is
 * reassurance, not terms anyone agrees to, all of it reaches a screen reader,
 * and a line in the pinned row would take the room it is pointing at.
 *
 * ── THE 999 LINE IS INSIDE IT, PINNED WITH THE BUTTON (review, 2026-09-24) ─
 * The scrim covers the R31 ribbon and `aria-modal` hides it from a screen
 * reader, and nothing but the button takes either away. So the panel restates
 * it (`copy.emergency`, `ConsentBanner`'s own sentence) in full-strength text,
 * in the pinned row under the button: the ribbon it stands in for never scrolls
 * away, so neither does this.
 *
 * ── THE CONFETTI FALLS BEHIND THE PANEL ────────────────────────────────────
 * It is rendered INSIDE the overlay, before the panel, and tokens.css drops it
 * to the overlay's bottom layer, so it falls over the scrim around the panel
 * and never over the words. Nothing between the overlay and the confetti
 * carries a transform, filter or backdrop-filter (the overlay's scrim is a
 * solid colour for exactly this reason), so the fixed layer stays anchored to
 * the screen.
 */
export function CelebrationDialog({
  route,
  onFinish,
}: {
  /** From the attach response's `paymentPhase` — never a query param. It picks the clinical words only. */
  route: PaymentCaptureRoute;
  /** The ONE way out: the button. */
  onFinish: () => void;
}) {
  const copy = celebrationCopy(route);
  const reduced = usePrefersReducedMotion();
  const titleId = useId();
  const leadId = useId();
  const trustId = useId();
  const inviteId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const moreBelow = useMoreBelow(panelRef, bodyRef);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const cta = ctaRef.current;
      if (!cta) return;
      // `preventScroll` keeps the PAGE where the view reset put it; the explicit
      // `scrollIntoView` then brings the button into view INSIDE the panel
      // (review finding, 2026-09-24: `preventScroll` alone stops every scrolling
      // ancestor, the panel included, and focus sat on a button nobody could
      // see — WCAG 2.4.7). The sticky actions row already keeps the button on
      // screen, so `nearest` normally does nothing. Optional call: jsdom
      // implements no layout.
      cta.focus({ preventScroll: true });
      cta.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const panel = panelRef.current;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // ESCAPE DOES NOT DISMISS — the button is the only way out. Stopped,
        // not merely ignored, so nothing underneath acts on it either.
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Off the controls entirely — on the panel itself (a click on its
      // background focuses it) or somewhere outside — Tab comes back to the
      // first control and Shift+Tab to the last; from the ends it wraps. The
      // selector is deliberately wider than today's one button: there is no
      // Escape to fall back on (ui/focusables.ts).
      if (panel) containTab(event, panel);
    }
    // Capture phase: this has to win before any page handler sees the key.
    document.addEventListener('keydown', handleKeyDown, true);

    // Lock the page behind, and restore EXACTLY what was there — an empty
    // string is not the same as "no inline overflow" (the ConsentBanner rule).
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div
      className="gps-jux-celebrate-overlay"
      data-testid="celebration-overlay"
      // NO onClick: a click outside the panel does nothing (CTO 2026-09-24).
      // A press on the scrim itself would otherwise move focus to <body>, off
      // the one button — so its default is prevented, and only there (a press
      // inside the panel still selects text and focuses what it should).
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) event.preventDefault();
      }}
      style={reduced ? undefined : { animation: 'gps-jux-celebrate-in 220ms var(--gps-ease) both' }}
    >
      <Confetti />
      <div
        ref={panelRef}
        className="gps-jux-celebrate-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={leadId}
        tabIndex={-1}
        // The pinned row's top edge (tokens.css) — measured, see useMoreBelow.
        data-more-below={moreBelow ? 'true' : 'false'}
      >
        <div ref={bodyRef} className="gps-jux-celebrate-body">
          <CelebrationArt
            className="gps-jux-celebrate-art"
            style={reduced ? undefined : { animation: 'gps-check-in 480ms var(--gps-ease) both' }}
          />
          <h2 id={titleId} className="gps-jux-celebrate-title">
            {copy.heading}
          </h2>
          <p id={leadId} className="gps-jux-celebrate-lead">
            <KeepCompoundsWhole text={copy.lead} />
          </p>
          <div className="gps-jux-celebrate-trust">
            <h3 id={trustId} className="gps-jux-celebrate-trust-heading">
              {copy.trustHeading}
            </h3>
            <ul aria-labelledby={trustId} className="gps-jux-celebrate-trust-list">
              {copy.trust.map((point) => (
                <li key={point.title} className="gps-jux-celebrate-trust-item">
                  <span className="gps-jux-celebrate-trust-icon">
                    <TrustIcon icon={point.icon} />
                  </span>
                  <span>
                    <strong className="gps-jux-celebrate-trust-title">
                      <KeepCompoundsWhole text={point.title} />
                    </strong>
                    <span className="gps-jux-celebrate-trust-body">
                      <KeepCompoundsWhole text={point.body} />
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <p id={inviteId} className="gps-jux-celebrate-invite">
            {copy.invite}
          </p>
        </div>
        <div className="gps-jux-celebrate-actions">
          {/* A native button (the kit `Button` forwards no ref) wearing the kit's
              own class + style, so it is the same CTA to look at and to hover. */}
          <button
            ref={ctaRef}
            type="button"
            className={buttonClass('cta')}
            style={{ ...buttonStyle('cta'), minHeight: '48px', fontSize: '15px' }}
            aria-describedby={inviteId}
            onClick={onFinish}
          >
            {copy.cta}
            <ButtonArrow />
          </button>
          {/* R31, restated: the scrim and `aria-modal` hide the ribbon until the
              button is pressed. In the PINNED row so it is on screen whenever the
              button is — the ribbon it stands in for never scrolls away either. */}
          <p className="gps-jux-celebrate-emergency">{copy.emergency}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Where focus lands when the celebration closes: the first thing the patient
 * has to do on the account step.
 *  - CODE-FIRST (a cold return: the emailed-code gate comes first) → the code box;
 *  - FORM-FIRST (in-flow: email and date of birth are already known and locked)
 *    → the first password box (`#reg-password`; the re-entry box follows it);
 *  - otherwise the first editable field in the step, so a layout this does not
 *    anticipate still lands somewhere useful;
 *  - and with NOTHING to type into yet (the account details are still loading)
 *    the step itself, made programmatically focusable, so the keyboard is on
 *    the new screen rather than dropped on `<body>` — the control that opened
 *    the modal (the pay button) no longer exists to go back to.
 */
export function focusAccountEntry(container: HTMLElement | null): void {
  if (!container) return;
  const field =
    container.querySelector<HTMLElement>('#restore-code') ??
    container.querySelector<HTMLElement>('#reg-password') ??
    container.querySelector<HTMLElement>('input:not([readonly]):not([disabled]):not([type="hidden"])');
  if (field) {
    field.focus();
    return;
  }
  if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
  container.focus({ preventScroll: true });
}

/* ── Wiring for CheckoutFlow ─────────────────────────────────────────────── */

/** What the account step needs to show the celebration and hand the keyboard on. */
export type PostPaymentCelebration = {
  /** On the account step's wrapper — where {@link focusAccountEntry} looks on close. */
  accountStepRef: RefObject<HTMLDivElement>;
  /**
   * Open it. Called from ONE place: the account branch of
   * `finaliseWithSetupIntent`, after a fresh, successful attach, for a guest.
   * A refresh or a resume onto the account step goes through the resume effect
   * instead, which never calls this — so the modal is a transition, never a
   * state a page can be reloaded into.
   */
  open: (route: PaymentCaptureRoute) => void;
  /** Has this checkout shown it? Handed to /thank-you as `celebrated`. */
  wasShown: () => boolean;
  /** The modal while it is open, else null — rendered inside the account step. */
  dialog: ReactNode;
};

/**
 * The celebration's state for `CheckoutFlow`, kept here so the checkout file
 * carries a handful of lines rather than a second state machine.
 *
 * FOCUS FOLLOWS THE CLOSE, INTO THE FORM. An effect rather than a call in the
 * button's handler because the focused button is removed as the modal
 * unmounts (focus falls to `<body>`), and the account form is only settled
 * once this commit is on screen; this commit's effect is the last word. The
 * modal takes NO amount (v2): `open` has no figure to pass.
 */
export function usePostPaymentCelebration(): PostPaymentCelebration {
  const [moment, setMoment] = useState<{ route: PaymentCaptureRoute } | null>(null);
  const shown = useRef(false);
  const focusOnClose = useRef(false);
  const accountStepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (moment || !focusOnClose.current) return;
    focusOnClose.current = false;
    focusAccountEntry(accountStepRef.current);
  }, [moment]);

  function finish() {
    focusOnClose.current = true;
    setMoment(null);
  }

  return {
    accountStepRef,
    open(route) {
      shown.current = true;
      setMoment({ route });
    },
    wasShown: () => shown.current,
    dialog: moment ? <CelebrationDialog route={moment.route} onFinish={finish} /> : null,
  };
}

/* ── Once per checkout, and only in the document that paid ───────────────── */

/**
 * TRUE from the moment checkout hands a just-completed order to /thank-you
 * until that page has mounted once. In MEMORY, deliberately:
 *  - a RELOAD of /thank-you starts a new document with this false, so it does
 *    not throw a second burst — the history entry still says `justCompleted`
 *    (it has to: it is what keeps the confirmation on screen), so it cannot
 *    tell a reload from the arrival on its own;
 *  - a later visit to the same entry in the same document finds it already
 *    settled;
 *  - and it is never written to the device (see the header — PECR reg. 6).
 * TWO THINGS READ IT (since review 2026-09-25): the confetti, and /thank-you's
 * `checkout_complete`, which fires only for an arrival in this document so a
 * reload or a non-bfcache Back does not count the same order twice. A new
 * navigation to /thank-you that skips `noteCheckoutHandOver()` would therefore
 * UNDERCOUNT conversions, not only lose a burst: `goToConfirmation` is the one
 * place that may navigate there.
 */
let handedOverInThisDocument = false;

/** Checkout is handing a just-completed order to /thank-you, in this document. */
export function noteCheckoutHandOver(): void {
  handedOverInThisDocument = true;
}

/** Did checkout hand an order over in THIS document? Read-only, so it is safe in a render or a state initialiser. */
export function checkoutHandedOverHere(): boolean {
  return handedOverInThisDocument;
}

/** /thank-you has taken the hand-over; any later mount is not the arrival. */
export function settleCheckoutHandOver(): void {
  handedOverInThisDocument = false;
}
